import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { Loading } from "@/components/Loading";
import { AffiliateApplications } from "@/components/admin/AffiliateApplications";

const AddAffiliate = ({ onDone }) => {
  const [f, setF] = useState({ name: "", email: "", code: "", payout_method: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/affiliates", f); toast.success("Affiliate added"); setF({ name: "", email: "", code: "", payout_method: "" }); onDone(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="paper-card grid gap-3 p-5 sm:grid-cols-5" data-testid="add-affiliate-form">
      {[["name", "Name"], ["email", "Email"], ["code", "Code (e.g. cheryl)"], ["payout_method", "Payout (PayPal email...)"]].map(([k, p]) => (
        <input key={k} className="field !min-h-0 !py-2 !text-base" placeholder={p} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} required={k !== "payout_method"} data-testid={`affiliate-${k}-input`} />
      ))}
      <button className="btn-primary !min-h-0 !py-2 !text-base" data-testid="affiliate-add-button">Add affiliate</button>
    </form>
  );
};

export const AdminAffiliates = () => {
  const [affs, setAffs] = useState(null);
  const [coms, setComs] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/affiliates").then(({ data }) => setAffs(data));
    api.get("/admin/commissions").then(({ data }) => setComs(data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (url) => { try { await api.post(url); toast.success("Updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const toggle = async (a) => { await api.patch(`/admin/affiliates/${a.affiliate_id}`, { active: !a.active }); load(); };
  if (!affs) return <Loading />;
  const origin = window.location.origin;
  return (
    <div className="space-y-8" data-testid="admin-affiliates">
      <AffiliateApplications onChange={load} />
      <AddAffiliate onDone={load} />
      <div className="overflow-x-auto paper-card">
        <table className="w-full text-left text-base">
          <thead className="bg-[#F2EBE1]"><tr>{["Affiliate", "Referral link / code", "Clicks", "Pending", "Review", "Batched", "Paid", "Void", ""].map((h) => <th key={h} className="px-3 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {affs.map((a) => (
              <tr key={a.affiliate_id} className="border-t border-[#E6DEC6]" data-testid={`affiliate-row-${a.code}`}>
                <td className="px-3 py-3">{a.name}<br /><span className="text-sm">{a.email}</span></td>
                <td className="px-3 py-3 text-sm"><code>{origin}/?ref={a.code}</code><br />code: <b>{a.code}</b></td>
                <td className="px-3 py-3">{a.clicks}</td>
                {["pending", "review", "batched", "paid", "void"].map((s) => <td key={s} className="px-3 py-3">{money(a.totals[s])}</td>)}
                <td className="px-3 py-3"><button className="btn-outline !min-h-0 !py-1" onClick={() => toggle(a)} data-testid={`affiliate-toggle-${a.code}`}>{a.active ? "Deactivate" : "Activate"}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!affs.length && <p className="p-6 text-[#5C4A3E]">No affiliates yet.</p>}
      </div>
      <div>
        <h3 className="text-2xl font-bold">Commissions</h3>
        <div className="mt-3 overflow-x-auto paper-card">
          <table className="w-full text-left text-base">
            <thead className="bg-[#F2EBE1]"><tr>{["Created", "Affiliate", "Order", "Source", "Amount", "Status", "Hold until", ""].map((h) => <th key={h} className="px-3 py-3">{h}</th>)}</tr></thead>
            <tbody>
              {coms.map((c) => (
                <tr key={c.commission_id} className="border-t border-[#E6DEC6]" data-testid={`commission-row-${c.commission_id}`}>
                  <td className="px-3 py-3 text-sm">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="px-3 py-3">{c.affiliate_code}</td>
                  <td className="px-3 py-3 text-sm">{c.order_id}</td>
                  <td className="px-3 py-3 text-sm">{c.source}</td>
                  <td className="px-3 py-3">{money(c.amount)}</td>
                  <td className="px-3 py-3 font-semibold">{c.status}{c.self_referral && <span className="ml-1 text-[#D97706]">(self-referral)</span>}{c.needs_review && <span className="ml-1 text-[#991B1B]">(refund after batching)</span>}</td>
                  <td className="px-3 py-3 text-sm">{new Date(c.hold_until).toLocaleDateString()}</td>
                  <td className="space-x-2 px-3 py-3">
                    {c.status === "review" && <button className="btn-outline !min-h-0 !py-1" onClick={() => act(`/admin/commissions/${c.commission_id}/approve`)} data-testid={`commission-approve-${c.commission_id}`}>Approve</button>}
                    {["pending", "review"].includes(c.status) && <button className="btn-outline !min-h-0 !py-1" onClick={() => act(`/admin/commissions/${c.commission_id}/void`)} data-testid={`commission-void-${c.commission_id}`}>Void</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!coms.length && <p className="p-6 text-[#5C4A3E]">No commissions yet.</p>}
        </div>
      </div>
    </div>
  );
};
